/**
 * Deployment-environment resolution (#767).
 *
 * `import.meta.env.MODE` / `PROD` describe the *build* mode, which is
 * `"production"` for every deployed image — including the one on
 * app-dev.bondit.dk. Anything user-facing that claims which environment the
 * app is running in must instead read `window.APP_CONFIG.ENVIRONMENT`, which
 * the Docker entrypoint injects per deployment (k8s `env.ENVIRONMENT`,
 * docker-compose for local containers). The Vite mode remains only as the
 * fallback for `npm run dev`, where no config.js exists and the build mode
 * ("development") happens to be the truth.
 *
 * The cluster can inject short-form values — Infisical's env slugs are
 * `dev`/`prod` and win over the chart ConfigMap (#772) — so common aliases
 * are normalized here rather than chased through gitops config. Anything
 * unrecognized passes through lowercased for the badge to render as-is.
 */
const ENVIRONMENT_ALIASES: Record<string, string> = {
  dev: "development",
  development: "development",
  prod: "production",
  production: "production",
};

export const resolveDeploymentEnvironment = (): string => {
  const raw = (window.APP_CONFIG?.ENVIRONMENT || import.meta.env.MODE).toLowerCase();
  return ENVIRONMENT_ALIASES[raw] ?? raw;
};

/** Where real work happens; the DEV marks link here (#1099). */
export const PRODUCTION_APP_URL = "https://app.bondit.dk";

/**
 * The short label the environment marks show (#1099), or `null` in
 * production, which carries no marks at all and looks exactly as before.
 * `development` reads "DEV"; any other non-production value is shown
 * uppercased, so an unexpected environment is marked rather than mistaken
 * for PROD.
 */
export const environmentMarkLabel = (
  environment: string = resolveDeploymentEnvironment()
): string | null => {
  if (environment === "production") {
    return null;
  }
  return environment === "development" ? "DEV" : environment.toUpperCase();
};
